(() => {
  const STANDARD_VARIANT_ID = 51050538893559;
  const EXPRESS_VARIANT_ID = 51050538926327;
  const STANDARD_CHARGE = 2500;
  const EXPRESS_CHARGE = 4900;
  // Grams per unit; inclusive merchant ranges. Gaps use ordinary Express.
  const EXPRESS_TIERS = [
    { min: 1500, max: 3000, variantId: 51256352473335, charge: 9900 },
    { min: 3500, max: 5000, variantId: 51256354898167, charge: 14900 },
    { min: 5500, max: 6500, variantId: 51256354930935, charge: 19900 },
    { min: 7000, max: 8500, variantId: 51256354963703, charge: 24900 },
    { min: 9000, max: 10500, variantId: 51256354996471, charge: 29900 },
    { min: 11000, max: 11500, variantId: 51256355029239, charge: 34900 },
    { min: 12000, max: 13000, variantId: 51256355062007, charge: 39900 },
  ];
  const expressRate = (item = {}) => EXPRESS_TIERS.find(
    (tier) => Number(item.grams) >= tier.min && Number(item.grams) <= tier.max,
  ) || { variantId: EXPRESS_VARIANT_ID, charge: EXPRESS_CHARGE };
  const FREE_STANDARD_THRESHOLD = 50000;
  const FEE_VARIANT_IDS = new Set([
    STANDARD_VARIANT_ID,
    EXPRESS_VARIANT_ID,
    ...EXPRESS_TIERS.map((tier) => tier.variantId),
  ]);
  const DELIVERY_EXEMPT_HANDLES = new Set(['gift-wrap', 'gift-wrapping']);
  const DELIVERY_EXEMPT_PROPERTY = '_Hadiyah delivery exempt';

  let syncPromise = null;

  const normalizePrice = (value) => {
    const price = Number(value);
    return Number.isFinite(price) ? Math.max(0, price) : 0;
  };

  const variantId = (item = {}) => Number(item.variant_id || item.id || 0);

  const isFeeItem = (item = {}) => FEE_VARIANT_IDS.has(variantId(item))
    || Number(item.product_id || item.product?.id) === 10059690017015
    || productHandle(item) === 'delivery-charge';

  const linePrice = (item = {}) => {
    const finalLinePrice = normalizePrice(item.final_line_price);
    if (finalLinePrice > 0) return finalLinePrice;

    const fallbackLinePrice = normalizePrice(item.line_price);
    if (fallbackLinePrice > 0) return fallbackLinePrice;

    const quantity = Math.max(0, Number(item.quantity) || 0);
    return normalizePrice(item.final_price || item.price) * quantity;
  };

  const propertyValue = (item, propertyName) => {
    const targetName = String(propertyName || '').trim().toLowerCase();
    const properties = item?.properties || {};
    const entry = Object.entries(properties).find(
      ([name]) => String(name || '').trim().toLowerCase() === targetName,
    );
    return String(entry?.[1] || '').trim();
  };

  const deliveryMethod = (item) =>
    propertyValue(item, 'Delivery Method') || propertyValue(item, 'Delivery Tier');

  const productHandle = (item = {}) => {
    const directHandle = String(item.handle || item.product_handle || '').trim().toLowerCase();
    if (directHandle) return directHandle;

    const productUrl = String(item.url || '').split('?')[0];
    const match = productUrl.match(/\/products\/([^/]+)\/?$/i);
    return String(match?.[1] || '').trim().toLowerCase();
  };

  const isDeliveryExemptItem = (item = {}) => {
    const exemption = propertyValue(item, DELIVERY_EXEMPT_PROPERTY).toLowerCase();
    return exemption === 'gift wrap' || DELIVERY_EXEMPT_HANDLES.has(productHandle(item));
  };

  const isExpressItem = (item) =>
    !isFeeItem(item) && deliveryMethod(item).toLowerCase() === 'express';

  const merchandiseItems = (cart = {}) => (cart.items || []).filter((item) => !isFeeItem(item));

  const deliveryCountry = () => {
    const sharedCountry = String(
      window.HadiyahLocalization?.getState?.()?.deliveryCountry || '',
    ).trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(sharedCountry)) return sharedCountry;

    try {
      const savedCountry = String(
        window.localStorage?.getItem('hadiyah:delivery-country-code') || '',
      ).trim().toUpperCase();
      if (/^[A-Z]{2}$/.test(savedCountry)) return savedCountry;
    } catch (error) {
      // Storage can be unavailable in privacy-restricted browsers.
    }

    const activeCountry = String(
      window.__hadiyahLocalizationConfig?.activeCountry || '',
    ).trim().toUpperCase();
    return /^[A-Z]{2}$/.test(activeCountry) ? activeCountry : 'IN';
  };

  const getSummary = (cart = {}) => {
    const items = merchandiseItems(cart);
    const deliveryItems = items.filter((item) => !isDeliveryExemptItem(item));
    const merchandiseSubtotal = items.reduce((total, item) => total + linePrice(item), 0);
    const merchandiseItemCount = items.reduce(
      (total, item) => total + Math.max(0, Number(item.quantity) || 0),
      0,
    );
    const expressFees = new Map([
      [EXPRESS_VARIANT_ID, 0],
      ...EXPRESS_TIERS.map((tier) => [tier.variantId, 0]),
    ]);
    let expressCharge = 0;
    deliveryItems.filter(isExpressItem).forEach((item) => {
      const rate = expressRate(item);
      const quantity = Math.max(0, Number(item.quantity) || 0);
      expressFees.set(rate.variantId, expressFees.get(rate.variantId) + quantity);
      expressCharge += rate.charge * quantity;
    });
    const expressItemCount = deliveryItems.reduce(
      (total, item) => total + (isExpressItem(item) ? Math.max(0, Number(item.quantity) || 0) : 0),
      0,
    );
    const standardItems = deliveryItems.filter(
      (item) => deliveryMethod(item).trim().toLowerCase() === 'standard',
    );
    const legacyItems = deliveryItems.filter((item) => !deliveryMethod(item));
    const legacySubtotal = legacyItems.reduce((total, item) => total + linePrice(item), 0);
    const standardPotentialQuantity = deliveryCountry() === 'IN'
      && (standardItems.length || legacyItems.length)
      ? 1
      : 0;
    const standardFeeQuantity = standardPotentialQuantity && merchandiseSubtotal <= FREE_STANDARD_THRESHOLD ? 1 : 0;
    const standardFreeQuantity = Math.max(0, standardPotentialQuantity - standardFeeQuantity);
    const isExpress = expressItemCount > 0;
    const hasProductSpecificDelivery = isExpress || standardItems.length > 0;
    const isStandardFree = standardPotentialQuantity > 0 && standardFeeQuantity === 0;
    const mode = isExpress && standardPotentialQuantity ? 'mixed' : isExpress ? 'express' : 'standard';
    const charge = expressCharge + (standardFeeQuantity * STANDARD_CHARGE);

    return {
      charge,
      expressItemCount,
      expressFees,
      hasProductSpecificDelivery,
      isExpress,
      isStandardFree,
      legacySubtotal,
      merchandiseItemCount,
      merchandiseItems: items,
      merchandiseSubtotal,
      mode,
      standardFeeQuantity,
      standardFreeQuantity,
      standardPotentialQuantity,
    };
  };

  const cartUrl = (path) => {
    const root = String(window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    return `${root}${path.replace(/^\//, '')}`;
  };

  const request = async (path, options = {}) => {
    const response = await fetch(cartUrl(path), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {}),
      },
      ...options,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.description || error.message || 'Unable to update the delivery charge.');
    }

    return response.json();
  };

  const fetchCart = () => request('cart.js');

  const publishCart = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return cart;
    window.cart = cart;
    window.CleanCartState?.publish?.(cart);
    return cart;
  };

  const syncNow = async (providedCart) => {
    let cart = providedCart && Array.isArray(providedCart.items) ? providedCart : await fetchCart();
    const summary = getSummary(cart);
    const feeItems = (cart.items || []).filter(isFeeItem);
    const updates = {};
    const additions = [];
    const requirements = [
      {
        mode: 'Standard',
        quantity: summary.standardFeeQuantity,
        variantId: STANDARD_VARIANT_ID,
      },
      ...[...summary.expressFees].map(([variantId, quantity]) => ({ mode: 'Express', quantity, variantId })),
    ];

    requirements.forEach((requirement) => {
      const matchingItems = feeItems.filter((item) => variantId(item) === requirement.variantId);
      const desiredItem = requirement.quantity > 0 ? matchingItems[0] : null;

      matchingItems.forEach((item) => {
        const desiredQuantity = item === desiredItem ? requirement.quantity : 0;
        if (Number(item.quantity || 0) !== desiredQuantity && item.key) {
          updates[item.key] = desiredQuantity;
        }
      });

      if (requirement.quantity > 0 && !desiredItem) {
        additions.push({
          id: requirement.variantId,
          quantity: requirement.quantity,
          properties: {
            '_Hadiyah delivery charge': requirement.mode,
          },
        });
      }
    });

    feeItems.filter((item) => !FEE_VARIANT_IDS.has(variantId(item))).forEach((item) => {
      if (item.key) updates[item.key] = 0;
    });

    if (Object.keys(updates).length) {
      cart = await request('cart/update.js', {
        method: 'POST',
        body: JSON.stringify({ updates }),
      });
    }

    if (additions.length) {
      await request('cart/add.js', {
        method: 'POST',
        body: JSON.stringify({ items: additions }),
      });
      cart = await fetchCart();
    }

    return publishCart(cart);
  };

  const sync = (cart) => {
    if (syncPromise) {
      return syncPromise.then(() => sync());
    }

    syncPromise = syncNow(cart).finally(() => {
      syncPromise = null;
    });
    return syncPromise;
  };

  window.HadiyahDeliveryCharge = Object.freeze({
    EXPRESS_CHARGE,
    EXPRESS_VARIANT_ID,
    FREE_STANDARD_THRESHOLD,
    STANDARD_CHARGE,
    STANDARD_VARIANT_ID,
    getSummary,
    isDeliveryExemptItem,
    isFeeItem,
    sync,
  });
})();
